import React, { useEffect, useMemo, useState } from "react";
import { mondayService } from "../../services/monday.service";

interface Column {
  id: string;
  title: string;
  type: string;
}

interface Item {
  id: string;
  name: string;
  column_values: { id: string; text: string }[];
}

interface Board {
  id: string;
  name: string;
  description: string;
  columns: Column[];
  items_page: { items: Item[] };
}

// Status badge colors — map common Monday status labels to colors
const STATUS_COLORS: Record<string, { bg: string; color: string }> = {
  done:          { bg: "#e6f9f0", color: "#1a9e5c" },
  completed:     { bg: "#e6f9f0", color: "#1a9e5c" },
  active:        { bg: "#e8f4fd", color: "#1976d2" },
  "in progress": { bg: "#fff8e1", color: "#f59e0b" },
  "working on it": { bg: "#fff3e0", color: "#e67e22" },
  stuck:         { bg: "#fde8e8", color: "#e53935" },
  blocked:       { bg: "#fde8e8", color: "#e53935" },
  lead:          { bg: "#f3e8fd", color: "#7b3fe4" },
  proposal:      { bg: "#e8f0fd", color: "#3b5bdb" },
  closed:        { bg: "#f0f0f0", color: "#777" },
};

function getStatusStyle(text: string): { bg: string; color: string } | null {
  const key = text.toLowerCase().trim();
  return STATUS_COLORS[key] ?? null;
}

const StatusBadge: React.FC<{ text: string }> = ({ text }) => {
  if (!text) return <span style={{ color: "#ccc" }}>—</span>;
  const style = getStatusStyle(text);
  if (!style) return <span style={{ color: "#444" }}>{text}</span>;
  return (
    <span
      style={{
        display: "inline-block",
        backgroundColor: style.bg,
        color: style.color,
        borderRadius: 20,
        padding: "2px 10px",
        fontSize: 12,
        fontWeight: 600,
        whiteSpace: "nowrap",
      }}
    >
      {text}
    </span>
  );
};

// Columns that should render as badges
const BADGE_TYPES = new Set(["color", "dropdown", "status"]);

const SetupCard: React.FC = () => (
  <div
    style={{
      backgroundColor: "#fff",
      borderRadius: 12,
      padding: "32px 40px",
      boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
      maxWidth: 600,
    }}
  >
    <h3 style={{ margin: "0 0 12px 0", color: "#1a1a2e" }}>Monday.com Not Configured</h3>
    <p style={{ color: "#666", marginBottom: 20 }}>
      Add these env vars to <code>backend/.env</code> and restart:
    </p>
    <pre
      style={{
        backgroundColor: "#f5f5f5",
        borderRadius: 6,
        padding: "16px 20px",
        fontSize: 13,
        lineHeight: 1.7,
        overflowX: "auto",
      }}
    >
{`MONDAY_API_TOKEN=your_personal_api_token
MONDAY_CRM_BOARD_ID=12345678
MONDAY_BOARD_IDS=12345678,87654321`}
    </pre>
  </div>
);

// Alternating column background colors (very light tints)
const COL_COLORS = [
  "#ffffff",
  "#f0f4ff",
];

const BoardTable: React.FC<{ board: Board; search: string }> = ({ board, search }) => {
  const items = board.items_page?.items ?? [];
  const columns = board.columns ?? [];

  const filtered = useMemo(() => {
    if (!search.trim()) return items;
    const q = search.toLowerCase();
    return items.filter((item) => {
      if (item.name.toLowerCase().includes(q)) return true;
      return item.column_values.some((cv) => cv.text?.toLowerCase().includes(q));
    });
  }, [items, search]);

  if (items.length === 0) {
    return <p style={{ color: "#999", fontStyle: "italic", padding: "20px 0" }}>No items in this board.</p>;
  }

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
        <thead>
          <tr>
            <th style={thStyle}>Name</th>
            {columns.map((col, colIdx) => {
              const colBg = COL_COLORS[colIdx % COL_COLORS.length];
              return <th key={col.id} style={{ ...thStyle, backgroundColor: colBg }}>{col.title}</th>;
            })}
          </tr>
        </thead>
        <tbody>
          {filtered.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + 1}
                style={{ padding: "24px", textAlign: "center", color: "#aaa", fontStyle: "italic" }}
              >
                No results for "{search}"
              </td>
            </tr>
          ) : (
            filtered.map((item) => {
              const colMap: Record<string, string> = {};
              for (const cv of item.column_values) {
                colMap[cv.id] = cv.text;
              }
              return (
                <tr key={item.id}>
                  <td style={{ ...tdStyle, fontWeight: 600, color: "#1a1a2e" }}>{item.name}</td>
                  {columns.map((col, colIdx) => {
                    const text = colMap[col.id] ?? "";
                    const isBadge = BADGE_TYPES.has(col.type);
                    const colBg = COL_COLORS[colIdx % COL_COLORS.length];
                    return (
                      <td key={col.id} style={{ ...tdStyle, backgroundColor: colBg }}>
                        {isBadge ? <StatusBadge text={text} /> : (text || <span style={{ color: "#ccc" }}>—</span>)}
                      </td>
                    );
                  })}
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
};

const thStyle: React.CSSProperties = {
  padding: "11px 16px",
  textAlign: "left",
  fontWeight: 700,
  fontSize: 12,
  color: "#888",
  textTransform: "uppercase",
  letterSpacing: 0.6,
  borderBottom: "1px solid #e8e8e8",
  whiteSpace: "nowrap",
  position: "sticky",
  top: 0,
};

const tdStyle: React.CSSProperties = {
  padding: "10px 16px",
  color: "#444",
  borderBottom: "1px solid #f0f0f0",
  verticalAlign: "middle",
  maxWidth: 280,
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const MondayBoards: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [configured, setConfigured] = useState(true);
  const [boards, setBoards] = useState<Board[]>([]);
  const [activeBoard, setActiveBoard] = useState<string>("");
  const [search, setSearch] = useState("");

  useEffect(() => {
    mondayService
      .getBoards()
      .then((data) => {
        console.log("[MondayBoards] response:", data);
        setConfigured(data.configured);
        setBoards(data.boards);
        if (data.boards.length > 0) setActiveBoard(data.boards[0].id);
      })
      .catch((err) => {
        console.error("[MondayBoards] fetch error:", err);
        setConfigured(false);
      })
      .finally(() => setLoading(false));
  }, []);

  const currentBoard = boards.find((b) => b.id === activeBoard);
  const itemCount = currentBoard?.items_page?.items?.length ?? 0;
  const filteredCount = useMemo(() => {
    if (!currentBoard || !search.trim()) return itemCount;
    const q = search.toLowerCase();
    return currentBoard.items_page.items.filter(
      (item) =>
        item.name.toLowerCase().includes(q) ||
        item.column_values.some((cv) => cv.text?.toLowerCase().includes(q)),
    ).length;
  }, [currentBoard, search, itemCount]);

  if (loading) {
    return (
      <div style={{ padding: "60px 0", textAlign: "center", color: "#bbb", fontSize: 15 }}>
        Loading Monday boards...
      </div>
    );
  }

  if (!configured) {
    return (
      <div>
        <h2 style={{ margin: "0 0 24px 0", color: "#1a1a2e", fontSize: 20 }}>Monday Boards</h2>
        <SetupCard />
      </div>
    );
  }

  return (
    <div>
      {/* Board tabs */}
      {boards.length > 1 && (
        <div
          style={{
            display: "flex",
            gap: 8,
            marginBottom: 20,
            flexWrap: "wrap",
          }}
        >
          {boards.map((board) => {
            const isActive = board.id === activeBoard;
            return (
              <button
                key={board.id}
                onClick={() => { setActiveBoard(board.id); setSearch(""); }}
                style={{
                  padding: "8px 18px",
                  border: isActive ? "2px solid #5B8FF9" : "2px solid #e0e0e0",
                  borderRadius: 20,
                  backgroundColor: isActive ? "#5B8FF9" : "#fff",
                  color: isActive ? "#fff" : "#555",
                  fontWeight: isActive ? 700 : 400,
                  fontSize: 13.5,
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {board.name}
              </button>
            );
          })}
        </div>
      )}

      {currentBoard && (
        <div
          style={{
            backgroundColor: "#fff",
            borderRadius: 12,
            boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
            overflow: "hidden",
          }}
        >
          {/* Table header bar */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "18px 20px",
              borderBottom: "1px solid #f0f0f0",
              gap: 16,
              flexWrap: "wrap",
            }}
          >
            <div>
              <span style={{ fontWeight: 700, fontSize: 16, color: "#1a1a2e" }}>
                {currentBoard.name}
              </span>
              {currentBoard.description && (
                <span style={{ marginLeft: 12, fontSize: 13, color: "#aaa" }}>
                  {currentBoard.description}
                </span>
              )}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span style={{ fontSize: 13, color: "#aaa", whiteSpace: "nowrap" }}>
                {search.trim() ? `${filteredCount} of ${itemCount}` : `${itemCount}`} items
              </span>
              {/* Search */}
              <div style={{ position: "relative" }}>
                <span
                  style={{
                    position: "absolute",
                    left: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    fontSize: 14,
                    color: "#bbb",
                    pointerEvents: "none",
                  }}
                >
                  🔍
                </span>
                <input
                  type="text"
                  placeholder="Search..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{
                    paddingLeft: 32,
                    paddingRight: 12,
                    paddingTop: 7,
                    paddingBottom: 7,
                    border: "1px solid #e0e0e0",
                    borderRadius: 20,
                    fontSize: 13.5,
                    outline: "none",
                    width: 200,
                    color: "#333",
                    backgroundColor: "#fafafa",
                    transition: "border-color 0.15s",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#5B8FF9")}
                  onBlur={(e) => (e.target.style.borderColor = "#e0e0e0")}
                />
              </div>
            </div>
          </div>

          {/* Table */}
          <BoardTable board={currentBoard} search={search} />
        </div>
      )}

      {boards.length === 0 && (
        <p style={{ color: "#999" }}>No boards found. Check your MONDAY_BOARD_IDS setting.</p>
      )}
    </div>
  );
};

export default MondayBoards;
